import React, { useCallback, useState, useEffect, useRef } from 'react';
import { extractSearchParameters, buildSearchPayload } from './travelAssistantUtils';

async function askAssistant(message, history = [], context = {}) {
  const baseUrl = process.env.REACT_APP_BACKEND_URL || '';
  const res = await fetch(`${baseUrl}/ai/mistral_proxy.php`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ message, history, context })
  });

  const data = await res.json();
  return data.reply || data.answer || 'Je peux vous aider à trouver le meilleur vol.';
}

function summarizeResults(results = []) {
  if (!results.length) return 'Je n’ai trouvé aucune offre correspondant à votre demande pour le moment.';

  const cheapest = [...results].sort((a, b) => (a.price?.total || 0) - (b.price?.total || 0))[0];
  const direct = results.find((flight) => flight.itineraries?.[0]?.segments?.length === 1);

  const parts = [];
  if (cheapest) parts.push(`Le prix le plus compétitif est ${cheapest.price?.total || 'n/d'} ${cheapest.price?.currency || ''}.`);
  if (direct) parts.push('Une offre directe est disponible.');
  return parts.join(' ');
}

export default function AITravelAssistant({ onSearch, onOpenSearch, voiceTrigger = false, onVoiceHandled }) {
  const [isOpen, setIsOpen] = useState(true);
  const [isMinimized, setIsMinimized] = useState(false);
  const [messages, setMessages] = useState([
    {
      sender: 'ai',
      text: 'Bonjour 👋 ! Je suis votre conseiller voyage. Dites-moi où vous voulez aller et quand, je m\'occupe du reste.',
      timestamp: new Date()
    }
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState([]);
  const [typing, setTyping] = useState(false);
  const [context, setContext] = useState({
    origin: 'ABJ',
    destination: '',
    departureDate: '',
    returnDate: '',
    adults: 1,
    children: 0,
    infants: 0,
    travelClass: 'ECONOMY',
    tripType: 'one-way',
    nonStop: false,
  });
  const [listening, setListening] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const recognitionRef = useRef(null);

  const quickSuggestions = [
    'Je voudrais aller à Paris',
    'Je veux aller à Dakar en famille',
    'Un vol pour Dubaï sans escale',
    'Je suis flexible sur les dates'
  ];

  const handleSend = useCallback(async (text = input) => {
    if (!text?.trim() || loading) return;
    const question = text.trim();
    const newHistory = [...history, { role: 'user', content: question }];

    setMessages((m) => [...m, { sender: 'user', text: question, timestamp: new Date() }]);
    setInput('');
    setLoading(true);
    setTyping(true);

    // Extraire les paramètres et les fusionner avec le contexte (origine = ABJ par défaut)
    const parsed = extractSearchParameters(question);
    const mergedContext = {
      ...context,
      origin: context.origin || 'ABJ', // Toujours ABJ par défaut sauf si l'utilisateur précise
      ...Object.fromEntries(Object.entries(parsed).filter(([, value]) => value !== null && value !== '' && value !== false))
    };

    setContext(mergedContext);

    // Vérifier si TOUS les champs obligatoires sont remplis (sauf returnDate qui dépend du type)
    const allSet = mergedContext.origin && mergedContext.destination && mergedContext.departureDate;
    const needReturn = mergedContext.tripType === 'round-trip';
    const returnOk = !needReturn || mergedContext.returnDate;

    let reply = '';

    if (allSet && (returnOk || !needReturn)) {
      // Tout est prêt → on lance la recherche
      const payload = buildSearchPayload(mergedContext);
      const recap = `📍 ${payload.originLocationCode} → ${payload.destinationLocationCode}\n📅 ${payload.departureDate}${payload.returnDate ? ` → ${payload.returnDate}` : ''}\n👤 ${payload.adults} adulte(s)${payload.children ? ` + ${payload.children} enfant(s)` : ''}${payload.infants ? ` + ${payload.infants} bébé(s)` : ''}${payload.travelClass !== 'ECONOMY' ? ` · ${payload.travelClass}` : ''}`;
      
      reply = `👍 Je récapitule :\n${recap}\n\nJe lance la recherche des meilleures offres...`;
      
      if (typeof onOpenSearch === 'function') onOpenSearch();
      try {
        const results = typeof onSearch === 'function' ? await onSearch(payload) : [];
        reply = `${reply}\n\n${summarizeResults(results)}`;
      } catch (error) {
        reply += '\n\nJe n\'ai pas pu récupérer les offres immédiatement, mais la recherche est lancée.';
      }
    }

    // Dans tous les cas, demander un commentaire naturel à l'IA
    // Elle saura quoi dire : récap, poser une question manquante, ou conseiller
    try {
      const assistantReply = await askAssistant(question, newHistory, mergedContext);
      if (assistantReply) {
        reply = reply ? `${reply}\n\n${assistantReply}` : assistantReply;
      }
    } catch (error) {
      if (!reply) reply = 'Je peux vous aider à trouver votre vol. Que me dites-vous ?';
    }

    setTyping(false);
    setTimeout(() => {
      setMessages((m) => [...m, { sender: 'ai', text: reply, timestamp: new Date() }]);
      setHistory([...newHistory, { role: 'assistant', content: reply }]);
    }, 400);

    setLoading(false);
  }, [context, history, input, loading, onOpenSearch, onSearch]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, typing]);

  useEffect(() => {
    if (isOpen && !isMinimized) {
      inputRef.current?.focus();
    }
  }, [isOpen, isMinimized]);

  useEffect(() => {
    if (!voiceTrigger) return;

    setIsOpen(true);
    setIsMinimized(false);

    const timer = window.setTimeout(() => {
      startVoiceInput();
      onVoiceHandled?.();
    }, 150);

    return () => window.clearTimeout(timer);
  }, [voiceTrigger, onVoiceHandled]);

  useEffect(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) return;

    recognitionRef.current = new SpeechRecognition();
    recognitionRef.current.lang = 'fr-FR';
    recognitionRef.current.continuous = false;
    recognitionRef.current.interimResults = false;

    recognitionRef.current.onresult = (event) => {
      const transcript = Array.from(event.results).map((result) => result[0]?.transcript).join(' ');
      setInput(transcript);
      setListening(false);
      if (transcript.trim()) handleSend(transcript.trim());
    };

    recognitionRef.current.onerror = () => {
      setListening(false);
    };

    recognitionRef.current.onend = () => {
      setListening(false);
    };
  }, [handleSend]);

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleSuggestionClick = (suggestion) => {
    handleSend(suggestion);
  };

  const clearChat = () => {
    setMessages([{ sender: 'ai', text: 'Conversation réinitialisée. Je peux vous aider à réserver un vol.', timestamp: new Date() }]);
    setHistory([]);
    setContext({
      origin: '',
      destination: '',
      departureDate: '',
      returnDate: '',
      adults: 1,
      children: 0,
      infants: 0,
      travelClass: 'ECONOMY',
      tripType: 'one-way',
      nonStop: false,
    });
  };

  const startVoiceInput = () => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition || !recognitionRef.current) {
      setMessages((m) => [...m, { sender: 'ai', text: 'La reconnaissance vocale n’est pas disponible sur ce navigateur.', timestamp: new Date() }]);
      return;
    }
    setListening(true);
    recognitionRef.current.start();
  };

  const stopVoiceInput = () => {
    recognitionRef.current?.stop();
    setListening(false);
  };

  if (!isOpen) {
    return (
      <button onClick={() => setIsOpen(true)} className="fixed bottom-6 right-6 z-50 bg-gradient-to-r from-blue-600 to-purple-600 text-white p-4 rounded-full shadow-2xl hover:scale-110 transition-all duration-300" aria-label="Ouvrir l’assistant">
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012-2h5l-5 5v-5z" />
        </svg>
      </button>
    );
  }

  return (
    <div className={`fixed bottom-6 right-6 z-50 w-[95vw] max-w-[430px] bg-white rounded-2xl shadow-2xl border border-gray-200 flex flex-col transition-all duration-300 ${isMinimized ? 'h-16' : 'h-[580px]'}`}>
      <div className="bg-gradient-to-r from-blue-600 via-blue-700 to-purple-600 text-white px-4 py-3 rounded-t-2xl font-bold flex items-center justify-between">
        <div>
          <div className="text-sm font-bold">Assistant Djater</div>
          <div className="text-xs opacity-90">Conseil intelligent • Recherche automatique</div>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setIsMinimized(!isMinimized)} className="text-white/90 hover:text-white hover:bg-white/20 rounded-lg w-8 h-8 flex items-center justify-center" aria-label={isMinimized ? 'Agrandir' : 'Réduire'}>{isMinimized ? '▲' : '▼'}</button>
          <button onClick={() => setIsOpen(false)} className="text-white/90 hover:text-white hover:bg-white/20 rounded-lg w-8 h-8 flex items-center justify-center" aria-label="Fermer">✕</button>
        </div>
      </div>

      {!isMinimized && (
        <>
          <div className="flex-1 p-3 overflow-y-auto space-y-3 bg-slate-50">
            {messages.map((msg, idx) => (
              <div key={idx} className={`flex ${msg.sender === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`flex items-end gap-2 max-w-[90%] ${msg.sender === 'user' ? 'flex-row-reverse' : 'flex-row'}`}>
                  <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-sm ${msg.sender === 'ai' ? 'bg-gradient-to-br from-blue-500 to-purple-500 text-white' : 'bg-slate-700 text-white'}`}>
                    {msg.sender === 'ai' ? '🤖' : '👤'}
                  </div>
                  <div className={`px-3 py-2 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap ${msg.sender === 'ai' ? 'bg-white border border-slate-200 text-slate-700' : 'bg-blue-600 text-white'}`}>
                    {msg.text}
                  </div>
                </div>
              </div>
            ))}
            {typing && (
              <div className="flex justify-start">
                <div className="bg-white border border-slate-200 rounded-2xl px-3 py-2 text-sm text-slate-500">L’IA réfléchit…</div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {messages.length === 1 && !loading && (
            <div className="px-3 pb-2">
              <div className="text-[11px] uppercase tracking-wide text-slate-500 mb-1">Suggestions rapides</div>
              <div className="flex flex-wrap gap-2">
                {quickSuggestions.map((suggestion, idx) => (
                  <button key={idx} onClick={() => handleSuggestionClick(suggestion)} className="text-xs bg-white border border-slate-200 hover:border-blue-400 hover:bg-blue-50 text-slate-700 px-2.5 py-1.5 rounded-full">{suggestion}</button>
                ))}
              </div>
            </div>
          )}

          <div className="border-t border-slate-200 p-3 bg-white rounded-b-2xl">
            <div className="flex items-center gap-2">
              <textarea value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={handleKeyPress} placeholder="Écrivez votre demande de voyage…" className="flex-1 px-3 py-2 border border-slate-200 rounded-xl resize-none focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm" rows={2} disabled={loading} />
              <button onClick={() => handleSend()} disabled={loading} className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-3 py-2 rounded-xl text-sm hover:opacity-90">{loading ? '…' : 'Envoyer'}</button>
            </div>
            <div className="flex items-center justify-between mt-2">
              <div className="flex gap-2">
                <button onClick={clearChat} className="text-xs text-slate-500 hover:text-blue-600">Réinitialiser</button>
                <button onClick={listening ? stopVoiceInput : startVoiceInput} className={`text-xs px-2 py-1 rounded-full ${listening ? 'bg-red-500 text-white' : 'bg-slate-100 text-slate-700'}`}>
                  {listening ? 'Arrêter l’écoute' : '🎤 Micro'}
                </button>
              </div>
              <button onClick={() => setIsMinimized(true)} className="text-xs text-slate-500 hover:text-blue-600">Réduire</button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
